import React from "react";
import { CheckCircle2, ArrowLeft, CreditCard, LayoutDashboard, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Button from "../../utils/Button";

const GradientBlob = ({ className }: { className?: string }) => (
  <div
    className={`absolute rounded-full blur-3xl opacity-40 ${className}`}
    aria-hidden="true"
  />
);

export default function PaymentMethodChangeSuccess() {
  const navigate = useNavigate();

  return (
    <div className="relative overflow-hidden min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <GradientBlob className="w-72 h-72 bg-emerald-300 top-10 -left-10" />
      <GradientBlob className="w-80 h-80 bg-indigo-200 bottom-10 right-10" />

      <div className="relative mx-auto max-w-3xl px-4 py-16">
        <div className="mb-8 flex items-center gap-2 text-sm text-gray-500">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm ring-1 ring-gray-200 hover:ring-gray-300 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <span className="text-gray-400">•</span>
          <span>Billing</span>
        </div>

        <div className="rounded-3xl bg-white shadow-xl ring-1 ring-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-500 to-neutral-500 text-white p-8">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.15em] text-white/80">
                  Payment Method Updated
                </p>
                <h1 className="text-2xl md:text-3xl font-semibold leading-tight">
                  Your payment method was changed successfully
                </h1>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            <div className="rounded-2xl border border-gray-100 bg-slate-50/70 p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Securely saved with Stripe</h3>
                  <p className="text-sm leading-relaxed text-gray-600 mt-1">
                    Your new card details are stored securely by Stripe. 123Vote never stores
                    your full card number on our servers.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Automatic billing</h3>
                  <p className="text-sm leading-relaxed text-gray-600 mt-1">
                    From now on, your subscription and any future charges will be billed
                    automatically to this card. No further action is required.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Button
                variant="primary"
                size="large"
                label="Back to dashboard"
                icon={<LayoutDashboard className="w-4 h-4" />}
                onClick={() => navigate("/partner/dashboard")}
                additionalClassname="w-full"
              />
              <Button
                variant="secondary"
                size="large"
                label="View payment history"
                icon={<CreditCard className="w-4 h-4" />}
                onClick={() => navigate("/partner/payments")}
                additionalClassname="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
